import React, { useState } from "react";
import { Button, Input } from "../../atoms";
import { Modal, FormField } from "../../molecules";

interface StartupOnboardingModalProps {
  isOpen: boolean;
  onCreateStartup: (data: {
    startupName: string;
    initialFunding?: number;
    industry?: string;
    description?: string;
  }) => Promise<void>;
  onSkip: () => void;
  isLoading?: boolean;
}

const INDUSTRIES = [
  "Technology",
  "Healthcare",
  "Finance",
  "E-commerce",
  "Education",
  "SaaS",
  "Consumer Products",
  "B2B Services",
  "Media & Entertainment",
  "Real Estate",
  "Food & Beverage",
  "Manufacturing",
  "Other",
];

export const StartupOnboardingModal: React.FC<StartupOnboardingModalProps> = ({
  isOpen,
  onCreateStartup,
  onSkip,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    startupName: "",
    initialFunding: "",
    industry: "",
    description: "",
  });

  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!formData.startupName.trim()) {
      setError("Startup name is required");
      return;
    }

    try {
      await onCreateStartup({
        startupName: formData.startupName,
        initialFunding: formData.initialFunding ? Number(formData.initialFunding) : undefined,
        industry: formData.industry || undefined,
        description: formData.description || undefined,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create startup");
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onSkip} title="Welcome! Let's set up your startup">
      <div className="space-y-6">
        <p className="text-sm text-white/65">
          To get started with BurnChart, we'll create your first startup. You can always add more later.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-[14px] border border-warn/40 bg-warn/10 p-3">
              <p className="text-sm text-warn">{error}</p>
            </div>
          )}

          <FormField label="Startup Name *" error="">
            <Input
              value={formData.startupName}
              onChange={(e) => handleChange("startupName", e.target.value)}
              placeholder="e.g., My Amazing Startup"
              disabled={isLoading}
            />
          </FormField>

          <FormField label="Initial Funding ($)" error="">
            <Input
              type="number"
              step="0.01"
              value={formData.initialFunding}
              onChange={(e) => handleChange("initialFunding", e.target.value)}
              placeholder="100000"
              disabled={isLoading}
            />
          </FormField>

          <FormField label="Industry" error="">
            <select
              value={formData.industry}
              onChange={(e) => handleChange("industry", e.target.value)}
              disabled={isLoading}
              className="sh-input"
            >
              <option value="">Select Industry (Optional)</option>
              {INDUSTRIES.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Description" error="">
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Brief description of your startup... (Optional)"
              disabled={isLoading}
              className="sh-input min-h-[80px] resize-y"
            />
          </FormField>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              variant="primary"
              size="md"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? "Creating..." : "Create Startup"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onSkip}
              disabled={isLoading}
            >
              Skip for now
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};